import React, { useMemo } from "react";
import { Box } from "@mui/material";
import {
  GpsFixed as GpsFixedIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { dtGreen } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";
import { TrackingSession, TrackingPosition } from "../../../shared/tracking/trackingSlice";
import ServiceProgressStepper from "./ServiceProgressStepper";
import LivePulseDot from "./LivePulseDot";
import { TrackingStatus } from "./map/TrackingMapContainer";

// ===================================================================
//  STATUS LABEL MAPPING
// ===================================================================

const getStatusLabel = (
  status: number | null,
  translate: (key: string) => string
): string => {
  if (status == null) return "";
  switch (status) {
    case TrackingStatus.Active:
      return translate("serviceTracking:status.enRoute");
    case TrackingStatus.Paused:
      return translate("serviceTracking:status.paused");
    case TrackingStatus.Arrived:
      return translate("serviceTracking:status.arrived");
    case TrackingStatus.Ended:
      return translate("serviceTracking:status.ended");
    default:
      return "";
  }
};

const getStatusColor = (status: number | null): string => {
  if (status == null) return dtGreen.textMuted;
  switch (status) {
    case TrackingStatus.Active:
      return dtGreen.green500;
    case TrackingStatus.Paused:
      return dtGreen.amber500;
    case TrackingStatus.Arrived:
      return dtGreen.green600;
    case TrackingStatus.Ended:
      return dtGreen.textSecondary;
    default:
      return dtGreen.textMuted;
  }
};

// ===================================================================
//  COMPONENT
// ===================================================================

interface ActiveServiceCardProps {
  session: TrackingSession;
  position: TrackingPosition | null;
  isSelected?: boolean;
  onClick?: (session: TrackingSession) => void;
}

const ActiveServiceCard: React.FC<ActiveServiceCardProps> = ({
  session,
  position,
  isSelected = false,
  onClick,
}) => {
  const { translate } = useTranslation(["serviceTracking"]);

  const etaMinutes = position?.etaMinutes ?? session.initialEtaMinutes ?? null;
  const statusLabel = getStatusLabel(session.status, translate);
  const statusColor = getStatusColor(session.status);
  const isArrived = session.status === TrackingStatus.Arrived;
  const isActive = session.status === TrackingStatus.Active;

  const initials = useMemo(() => {
    const name = session.providerName || "";
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase() || "??";
  }, [session.providerName]);

  const handleClick = () => {
    if (onClick) onClick(session);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        backgroundColor: dtGreen.cardBg,
        border: `1px solid ${isSelected ? dtGreen.green500 : dtGreen.cardBorder}`,
        borderRadius: dtGreen.radius,
        padding: "14px",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s ease",
        boxShadow: isSelected
          ? `0 0 0 2px ${dtGreen.greenGlow}, ${dtGreen.cardShadow}`
          : dtGreen.cardShadow,
        "&:hover": onClick
          ? {
              borderColor: dtGreen.green400,
              boxShadow: dtGreen.cardShadowHover,
              transform: "translateY(-1px)",
            }
          : {},
      }}
    >
      {/* Header row: provider info + status */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "10px",
        }}
      >
        {/* Provider avatar + name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
          {/* Avatar circle */}
          <Box
            sx={{
              width: 36,
              height: 36,
              minWidth: 36,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${dtGreen.green500}, ${dtGreen.green700})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              fontFamily: dtGreen.font,
              fontSize: "13px",
              fontWeight: 700,
            }}
          >
            {initials}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontFamily: dtGreen.font,
                fontSize: "14px",
                fontWeight: 600,
                color: dtGreen.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {session.providerName}
            </Box>
            {session.serviceRequestInternalNumber && (
              <Box
                sx={{
                  fontFamily: dtGreen.fontMono,
                  fontSize: "11px",
                  color: dtGreen.textMuted,
                  mt: "1px",
                }}
              >
                #{session.serviceRequestInternalNumber}
              </Box>
            )}
          </Box>
        </Box>

        {/* Status badge */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 10px",
            borderRadius: dtGreen.radiusPill,
            backgroundColor: `${statusColor}14`,
            border: `1px solid ${statusColor}33`,
            flexShrink: 0,
          }}
        >
          {isActive && <LivePulseDot size={6} color={statusColor} />}
          <Box
            component="span"
            sx={{
              fontFamily: dtGreen.font,
              fontSize: "11px",
              fontWeight: 600,
              color: statusColor,
              whiteSpace: "nowrap",
            }}
          >
            {statusLabel}
          </Box>
        </Box>
      </Box>

      {/* Service request issue */}
      {session.serviceRequestIssue && (
        <Box
          sx={{
            fontFamily: dtGreen.font,
            fontSize: "13px",
            color: dtGreen.textSecondary,
            mb: "10px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {session.serviceRequestIssue}
        </Box>
      )}

      {/* ETA row */}
      {!isArrived && etaMinutes != null && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            mb: "12px",
          }}
        >
          <ScheduleIcon
            sx={{ fontSize: "16px", color: dtGreen.green500 }}
          />
          <Box
            sx={{
              fontFamily: dtGreen.fontMono,
              fontSize: "14px",
              fontWeight: 600,
              color: dtGreen.green600,
            }}
          >
            ~{etaMinutes} {translate("serviceTracking:minutes")}
          </Box>
        </Box>
      )}

      {/* Progress stepper */}
      <ServiceProgressStepper trackingStatus={session.status} />
    </Box>
  );
};

export default React.memo(ActiveServiceCard);
